import React, { useState, useEffect } from 'react';
import { ArrowLeft, HelpCircle, Upload } from 'lucide-react';
import { useApp } from './AppContext';
import { ContractDataIntroOverlay } from './ContractDataIntroOverlay';
import { useTranslation } from '../i18n/TranslationContext';

interface ContractDataState {
  hasSeenIntro: boolean;
  showIntroOverlay: boolean;
  isFirstVisit: boolean;
}

export function ContractData() {
  const { t } = useTranslation();
  const { navigateDirectly, navigateToProfileCompletion, updateUserCompletionState, userProfile, setUserProfile, navigationContext } = useApp();
  const [formData, setFormData] = useState({
    employmentType: '',
    employmentDocument: null as File | null
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Contract data intro overlay state
  const [overlayState, setOverlayState] = useState<ContractDataState>({
    hasSeenIntro: false,
    showIntroOverlay: false,
    isFirstVisit: true
  });

  // Initialize first-time check
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('hasSeenContractIntro');
    const isFirstTime = !hasSeenIntro;
    
    setOverlayState({
      hasSeenIntro: !!hasSeenIntro,
      isFirstVisit: isFirstTime,
      showIntroOverlay: isFirstTime
    });
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, employmentDocument: file }));
    // Clear error when file is selected
    if (errors.employmentDocument) {
      setErrors(prev => ({ ...prev, employmentDocument: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.employmentType.trim()) {
      newErrors.employmentType = t('contractData.validation.employmentTypeRequired');
    }
    
    // Only require document for full-time employed
    if (formData.employmentType === 'Full-time employed' && !formData.employmentDocument) {
      newErrors.employmentDocument = t('contractData.validation.documentRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDownloadTemplate = () => {
    // In a real implementation, this would download the actual template
    const link = document.createElement('a');
    link.href = '#'; // Replace with actual template URL
    link.download = 'employment-permission-template.pdf';
    link.click();
  };

  const handleOverlayClose = () => {
    setOverlayState(prev => ({
      ...prev,
      hasSeenIntro: true,
      showIntroOverlay: false,
      isFirstVisit: false
    }));
    localStorage.setItem('hasSeenContractIntro', 'true');
  };

  const handleHelpIconClick = () => {
    setOverlayState(prev => ({
      ...prev,
      showIntroOverlay: true
    }));
  };

  const handleBackClick = () => {
    // Return to the source screen (ProfileCompletionOverview)
    if (navigationContext?.sourceScreen) {
      navigateDirectly(navigationContext.sourceScreen);
    } else {
      navigateDirectly('app-profile-completion-overview');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Mark contract data as complete
    updateUserCompletionState({ 
      contract_complete: true,
      profile_complete: true 
    });

    // Navigate back to source screen
    if (navigationContext?.sourceScreen) {
      navigateDirectly(navigationContext.sourceScreen);
    } else {
      navigateDirectly('app-profile-completion-overview');
    }
  };

  return (
    <div className="contract-data-screen bg-[var(--ucm-bg-gray)] min-h-screen">
      {/* Header with Back Button and Help Icon */}
      <div className="screen-header bg-white border-b border-[var(--ucm-border-light)] px-4 py-3 md:px-6 md:py-4">
        <button 
          className="back-button p-2 hover:bg-[var(--ucm-light-gray)] rounded-lg transition-colors"
          onClick={handleBackClick}
          aria-label="Zurück gehen"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        <h1 className="screen-title text-lg md:text-xl font-semibold text-[var(--ucm-text-dark)] flex-1 text-center">{t('contractData.title')}</h1>
        
        <button 
          className="help-button p-2 hover:bg-[var(--ucm-light-gray)] rounded-lg transition-colors"
          onClick={handleHelpIconClick}
          aria-label="Hilfeinformationen anzeigen"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      {/* Main content wrapper */}
      <div className="contract-form-content px-4 py-6 md:px-6 md:py-8 max-w-2xl mx-auto">
        <div className="mb-6">
          <p className="text-[var(--ucm-text-medium)] text-center text-sm md:text-base">
            {t('contractData.subtitle')}
          </p>
        </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Employment Information */}
        <div className="bg-white rounded-2xl p-4 md:p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
          <div className="space-y-4 md:space-y-6">
            {/* Employment Type Selection */}
            <div>
              <label htmlFor="employmentType" className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
                {t('contractData.currentEmploymentStatus')}
              </label>
              <select
                id="employmentType"
                value={formData.employmentType}
                onChange={(e) => handleInputChange('employmentType', e.target.value)}
                className={`w-full px-3 py-3 md:px-4 md:py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors text-sm md:text-base ${
                  errors.employmentType ? 'border-[var(--ucm-error-red)]' : 'border-[var(--ucm-border-light)]'
                }`}
              >
                <option value="">{t('contractData.selectEmploymentType')}</option>
                <option value="Full-time employed">{t('contractData.employmentTypes.fullTime')}</option>
                <option value="Part-time employed">{t('contractData.employmentTypes.partTime')}</option>
                <option value="Freelancer">{t('contractData.employmentTypes.freelancer')}</option>
                <option value="Student">{t('contractData.employmentTypes.student')}</option>
                <option value="Unemployed">{t('contractData.employmentTypes.unemployed')}</option>
                <option value="Retired">{t('contractData.employmentTypes.retired')}</option>
              </select>
              {errors.employmentType && (
                <p className="mt-1 text-xs md:text-sm text-[var(--ucm-error-red)]">{errors.employmentType}</p>
              )}
            </div>

            {/* Conditional File Upload Field */}
            {formData.employmentType === 'Full-time employed' && (
              <div>
                <label htmlFor="employmentDocument" className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
                  {t('contractData.employmentPermissionDocument')}
                </label>
                <p className="text-xs md:text-sm text-[var(--ucm-text-light)] mb-2">
                  {t('contractData.documentRequired')}
                </p>
                <div className="relative">
                  <input
                    type="file"
                    id="employmentDocument"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                  />
                  <label
                    htmlFor="employmentDocument"
                    className={`w-full px-3 py-3 md:px-4 md:py-3 border rounded-xl focus:outline-none cursor-pointer transition-colors flex items-center justify-between text-sm md:text-base ${
                      errors.employmentDocument ? 'border-[var(--ucm-error-red)]' : 'border-[var(--ucm-border-light)]'
                    } hover:border-[var(--ucm-primary-teal)]`}
                  >
                    <span className={`${formData.employmentDocument ? 'text-[var(--ucm-text-dark)]' : 'text-[var(--ucm-text-light)]'} truncate pr-2`}>
                      {formData.employmentDocument ? formData.employmentDocument.name : t('contractData.uploadDocument')}
                    </span>
                    <Upload className="w-4 h-4 md:w-5 md:h-5 text-[var(--ucm-text-light)] flex-shrink-0" />
                  </label>
                </div>
                {errors.employmentDocument && (
                  <p className="mt-1 text-xs md:text-sm text-[var(--ucm-error-red)]">{errors.employmentDocument}</p>
                )}
              </div>
            )}

            {/* Template Download Section */}
            <div>
              <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
                {t('contractData.needTemplate')}
              </label>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="px-4 py-2 md:px-6 md:py-2 bg-white border border-[var(--ucm-border-light)] text-[var(--ucm-text-dark)] rounded-xl hover:border-[var(--ucm-primary-teal)] hover:bg-[var(--ucm-light-gray)] transition-colors text-sm md:text-base"
              >
                {t('contractData.downloadTemplate')}
              </button>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full h-12 md:h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200 text-sm md:text-base"
        >
          {t('contractData.save')}
        </button>
      </form>
      </div>

      {/* Overlay Component */}
      <ContractDataIntroOverlay
        isVisible={overlayState.showIntroOverlay}
        onClose={handleOverlayClose}
        isFirstTime={overlayState.isFirstVisit}
      />
    </div>
  );
}